import { useEffect, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  listBroadcasts,
  createBroadcast,
  activateBroadcast,
  deactivateBroadcast,
  type Broadcast,
} from '../lib/admin';

// Admin composer for in-app broadcast messages. One message at most is
// active at any time — activating one deactivates the rest server-side.
// Clients poll GET /api/settings (~2 min in the app), so a newly activated
// message reaches everyone within a couple of minutes; dismissed users see
// it again after the app's 15-minute snooze for as long as it stays active.
export function AdminBroadcasts() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();

  const [history, setHistory] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null); // toggle in flight
  const [err, setErr] = useState<string | null>(null);

  // Composer state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<'info' | 'update'>('info');
  const [minVersion, setMinVersion] = useState('');
  const [activateOnCreate, setActivateOnCreate] = useState(true);
  const [creating, setCreating] = useState(false);

  async function reload() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      setHistory(await listBroadcasts(accessToken));
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const active = history.find((b) => b.active) ?? null;

  async function submit(e: Event) {
    e.preventDefault();
    if (!accessToken || creating) return;
    if (!title.trim() || !body.trim()) {
      setErr('Title and body are required.');
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      await createBroadcast(accessToken, {
        title: title.trim(),
        body: body.trim(),
        kind,
        minVersion: kind === 'update' ? minVersion.trim() || undefined : undefined,
        activate: activateOnCreate,
      });
      setTitle('');
      setBody('');
      setMinVersion('');
      await reload();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setCreating(false);
    }
  }

  async function toggle(b: Broadcast) {
    if (!accessToken || busyId !== null) return;
    setBusyId(b.id);
    setErr(null);
    try {
      if (b.active) {
        await deactivateBroadcast(accessToken, b.id);
      } else {
        await activateBroadcast(accessToken, b.id);
      }
      await reload();
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Broadcasts</h1>
            <div class="admin-who" style={{ marginLeft: 'auto' }}>
              <span class="admin-mono">
                {active ? 'one active' : 'none active'}
              </span>
            </div>
          </header>

          <p class="admin-lede">
            In-app message shown to every user as a dismissable popup. Only one
            can be active at a time; dismissed users see it again after ~15
            minutes for as long as it stays active. Kind <strong>update</strong>{' '}
            adds a store button in the app and, when a min version is set, only
            shows to builds older than it.
          </p>

          {err && <p class="admin-error">{err}</p>}

          {/* ── Composer ── */}
          <form onSubmit={submit} style={composerStyle}>
            <div style={rowStyle}>
              <input
                class="admin-filter-input"
                type="text"
                placeholder="Title (e.g. Update available)"
                value={title}
                maxLength={160}
                onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
                style={{ flex: 2, minWidth: 220 }}
              />
              <select
                class="admin-filter-input"
                value={kind}
                onChange={(e) =>
                  setKind((e.target as HTMLSelectElement).value as 'info' | 'update')
                }
                style={{ width: 130 }}
              >
                <option value="info">info</option>
                <option value="update">update</option>
              </select>
              {kind === 'update' && (
                <input
                  class="admin-filter-input"
                  type="text"
                  placeholder="Min version (e.g. 1.0.4) — empty = everyone"
                  value={minVersion}
                  onInput={(e) => setMinVersion((e.target as HTMLInputElement).value)}
                  style={{ width: 260 }}
                />
              )}
            </div>
            <textarea
              class="admin-filter-input"
              placeholder="Message body…"
              value={body}
              rows={3}
              onInput={(e) => setBody((e.target as HTMLTextAreaElement).value)}
              style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={rowStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={activateOnCreate}
                  onChange={(e) =>
                    setActivateOnCreate((e.target as HTMLInputElement).checked)
                  }
                />
                <span>Activate immediately{active ? ' (replaces the current one)' : ''}</span>
              </label>
              <button
                class="admin-link"
                type="submit"
                disabled={creating || !title.trim() || !body.trim()}
                style={{ marginLeft: 'auto' }}
              >
                {creating ? 'Creating…' : activateOnCreate ? 'Create & activate' : 'Create draft'}
              </button>
            </div>
          </form>

          {/* ── History ── */}
          {loading && history.length === 0 && <p class="admin-lede">Loading…</p>}
          {!loading && history.length === 0 && (
            <p class="admin-lede">No broadcasts yet — the first one you create shows up here.</p>
          )}

          {history.map((b) => (
            <article key={b.id} style={b.active ? activeCardStyle : cardStyle}>
              <header style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={b.active ? liveChipStyle : draftChipStyle}>
                    {b.active ? 'ACTIVE' : 'off'}
                  </span>
                  <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.title}
                  </strong>
                  <span style={kindChipStyle}>{b.kind}</span>
                  {b.minVersion && (
                    <span class="admin-mono" style={{ fontSize: 11, opacity: 0.6 }}>
                      &lt; v{b.minVersion}
                    </span>
                  )}
                </div>
                <button
                  class="admin-link"
                  onClick={() => toggle(b)}
                  disabled={busyId !== null}
                  style={b.active ? deactivateBtnStyle : activateBtnStyle}
                >
                  {busyId === b.id ? '…' : b.active ? 'Deactivate' : 'Activate'}
                </button>
              </header>
              <p style={bodyTextStyle}>{b.body}</p>
              <div class="admin-mono" style={{ fontSize: 11, opacity: 0.55 }}>
                #{b.id} · created {fmtDate(b.createdAt)}
                {b.activatedAt && <> · activated {fmtDate(b.activatedAt)}</>}
                {!b.active && b.deactivatedAt && <> · deactivated {fmtDate(b.deactivatedAt)}</>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </RequireAdmin>
  );
}

function fmtDate(iso: string): string {
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

// ── Inline styles, same local-to-page trick as the other admin pages. ──

const composerStyle: Record<string, string | number> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 16px',
  border: '1px solid rgba(245, 237, 224, 0.12)',
  borderRadius: 12,
  background: 'rgba(245, 237, 224, 0.03)',
  margin: '0 0 20px',
};

const rowStyle: Record<string, string | number> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  alignItems: 'center',
};

const cardStyle: Record<string, string | number> = {
  border: '1px solid rgba(245, 237, 224, 0.1)',
  borderRadius: 10,
  background: 'rgba(245, 237, 224, 0.02)',
  padding: '12px 16px',
  margin: '0 0 10px',
};

const activeCardStyle: Record<string, string | number> = {
  ...cardStyle,
  border: '1px solid rgba(232, 123, 30, 0.5)',
  background: 'rgba(232, 123, 30, 0.05)',
};

const cardHeaderStyle: Record<string, string | number> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 6,
};

const liveChipStyle: Record<string, string | number> = {
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 10,
  letterSpacing: '0.08em',
  color: '#f0a64e',
  padding: '2px 8px',
  borderRadius: 9999,
  border: '1px solid rgba(232, 123, 30, 0.5)',
  background: 'rgba(232, 123, 30, 0.12)',
};

const draftChipStyle: Record<string, string | number> = {
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 10,
  letterSpacing: '0.08em',
  opacity: 0.5,
  padding: '2px 8px',
  borderRadius: 9999,
  border: '1px solid rgba(245, 237, 224, 0.2)',
};

const kindChipStyle: Record<string, string | number> = {
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 10,
  letterSpacing: '0.06em',
  opacity: 0.7,
  padding: '2px 8px',
  borderRadius: 9999,
  border: '1px solid rgba(245, 237, 224, 0.15)',
};

const activateBtnStyle: Record<string, string | number> = {
  background: 'rgba(64, 160, 80, 0.15)',
  border: '1px solid rgba(64, 160, 80, 0.5)',
  color: '#7fd095',
  padding: '6px 12px',
  borderRadius: 6,
  whiteSpace: 'nowrap',
};

const deactivateBtnStyle: Record<string, string | number> = {
  background: 'rgba(200, 60, 60, 0.12)',
  border: '1px solid rgba(200, 60, 60, 0.5)',
  color: '#e88080',
  padding: '6px 12px',
  borderRadius: 6,
  whiteSpace: 'nowrap',
};

const bodyTextStyle: Record<string, string | number> = {
  margin: '0 0 8px',
  fontSize: 14,
  lineHeight: 1.45,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  opacity: 0.85,
};
